import React from "react";
import {
    Alert,
    Button,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {CloudSyncOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import type {PtV2Dataset, PtV2ImportRecord} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {parseImportErrors} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatDate, formatInt, MISSING, periodLabel} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2SyncMutation} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";
import {PtV2ImportPanel} from "~/experiments/pages/payment-terms-v2/components/PtV2ImportPanel.tsx";
import {PtV2ImportHistory} from "~/experiments/pages/payment-terms-v2/components/PtV2ImportHistory.tsx";

type SyncResult = {kind: 'ok'; run: PtV2ImportRecord} | {kind: 'failed'; message: string};

function syncError(e: unknown): string {
    const data = (e as {response?: {status?: number; data?: unknown}}).response?.data;
    const errors = parseImportErrors(data);
    return errors.length ? errors.map(x => x.file ? `${x.file}: ${x.message}` : x.message).join(' · ') : 'The sync failed. Try again.';
}

/**
 * Mithra staff only (`authStore.isMithraStaffGroup`; the backend enforces it too): sync from Hermes (BigQuery),
 * the zip fallback, the run history and the loaded dataset versions.
 */
export const PtV2DataManagement: React.FC<{datasets: PtV2Dataset[]}> = ({datasets}) => {
    const syncM = usePtV2SyncMutation();
    const [result, setResult] = React.useState<SyncResult | null>(null);

    const sync = async () => {
        setResult(null);
        try {
            setResult({kind: 'ok', run: await syncM.sync()});
        } catch (e) {
            setResult({kind: 'failed', message: syncError(e)});
        }
    };
    const synced = result?.kind === 'ok' ? result.run.datasets?.length ?? Object.keys(result.run.versions ?? {}).length : 0;

    return <PtV2Card title="Data management" right={<Tag color="info" label="Mithra staff only"/>}>
        <div className="ptv2-import">
            <div className="ptv2-eyebrow">Hermes</div>
            <Typography variant="body2" className="ptv2-muted">
                Pull the latest confirmed workpapers that Hermes published to BigQuery. A nightly job does the same.
            </Typography>
            <div className="ptv2-import-row">
                <Button variant="contained" size="small" startIcon={<CloudSyncOutlined/>}
                        disabled={syncM.isPending} onClick={() => void sync()}>
                    Sync from Hermes
                </Button>
            </div>
            {syncM.isPending && <LinearProgress/>}
            {result?.kind === 'ok' && <Alert severity="success">
                Synced {formatInt(synced)} datasets from Hermes.
                {result.run.skipped?.length > 0 && <> Skipped unknown workpapers: {result.run.skipped.join(', ')}.</>}
            </Alert>}
            {result?.kind === 'failed' && <Alert severity="error">{result.message}</Alert>}

            <div className="ptv2-eyebrow">Zip upload (fallback)</div>
            <PtV2ImportPanel/>

            <PtV2ImportHistory/>

            <div className="ptv2-eyebrow">Loaded datasets</div>
            {datasets.length === 0
                ? <Typography variant="body2" className="ptv2-muted">No datasets loaded yet.</Typography>
                : <Table size="small" aria-label="Loaded datasets">
                    <TableHead>
                        <TableRow>
                            <TableCell>Dataset</TableCell>
                            <TableCell align="right">Version</TableCell>
                            <TableCell>Published</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell align="right">Rows</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {datasets.map(d => <TableRow key={d.dataset}>
                            <TableCell>
                                <div>{d.title ?? d.dataset}</div>
                                <div className="ptv2-mono ptv2-faint">{d.slug}</div>
                            </TableCell>
                            <TableCell align="right" className="ptv2-num">{d.version != null ? `v${d.version}` : MISSING}</TableCell>
                            <TableCell className="ptv2-num">{formatDate(d.publishedAt)}</TableCell>
                            <TableCell>
                                <Tag color={d.source === 'bigquery' ? 'info' : 'default'} variant="outlined"
                                     label={d.source === 'bigquery' ? 'BigQuery' : 'Zip'}/>
                            </TableCell>
                            <TableCell className="ptv2-num">{periodLabel(d.periodStart, d.periodEnd) ?? MISSING}</TableCell>
                            <TableCell align="right" className="ptv2-num">{formatInt(d.rowCount)}</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>}
        </div>
    </PtV2Card>;
};
